<template>
  <v-row no-gutters ref="widget">
    <!-- loader -->
    <template v-if="loaderMain">
      <v-progress-circular class="v-progress-circular-fixed" indeterminate color="secondary" />
    </template>
    <!-- end loader -->

    <!-- errors -->
    <template v-else-if="error">
      <v-col cols="12" class="pa-8 text-center">

        <template v-if="error === 'base_is_not_exist'">
          <v-icon color="red darken-1" large>mdi-alert</v-icon>
          <span class="d-block body-1 font-weight-medium mt-4">{{$t('cb.pageIsNotExistsMessage')}}</span>
          <span class="d-block body-2">Verifica la URL que te trajo hasta acá</span>
        </template>

      </v-col>
    </template>
    <!-- end errors -->

    <!-- link -->
    <template v-else>
      <v-col cols="12" sm="6">
        <amount-sheet />
      </v-col>
      <v-col cols="12" sm="6">
        <v-sheet class="my-3 mx-auto overflow-hidden" rounded outlined>
          <v-card-text>
            <v-row>
              <v-col class="py-2" cols="12">
                <span class="d-block ml-1 text--secondary font-weight-medium">Información de facturación</span>
              </v-col>
              <v-col class="py-2" cols="12">
                <v-text-field v-model.trim="$v.customer.name.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.name.$error" hide-details :label="fieldLabels.name" maxlength="64" outlined persistent-hint required />
              </v-col>
              <v-col class="py-2" cols="12">
                <v-text-field v-model.trim="$v.customer.address.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.address.$error" hide-details label="Dirección" maxlength="64" outlined persistent-hint required />
              </v-col>
              <v-col class="py-2" cols="6">
                <v-autocomplete v-model="$v.customer.country.$model" :color="instance.account.color_background" dense :error="$v.customer.country.$error" hide-details :items="choices.countries" item-value="id" item-text="name" label="País" outlined persistent-hint required>
                  <template v-slot:item="data">
                    <v-list-item-title class="font-weight-regular">{{data.item.name}}</v-list-item-title>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col class="py-2" cols="6">
                <v-autocomplete v-model="$v.customer.state.$model" :color="instance.account.color_background" dense :error="$v.customer.state.$error" hide-details :items="choices.states" item-value="id" item-text="name" :label="fieldLabels.state" no-data-text="No existen estados para el país." outlined persistent-hint required>
                  <template v-slot:item="data">
                    <v-list-item-title class="font-weight-regular">{{data.item.name}}</v-list-item-title>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- BR tax settings fields -->
              <template v-if="instance.account.country === 'BR' && customer.country === 'BR'">
                <v-col class="py-2" cols="12">
                  <v-autocomplete v-model="$v.customer.tax_settings.br_city_code.$model" @keyup="filterChoices('brMunicipality', 'choices.brMunicipalities')" :color="instance.account.color_background" dense :error="$v.customer.tax_settings.br_city_code.$error" hide-details item-text="name" item-value="id" :items="filteredChoices.brMunicipality.items" label="Municipio" no-data-text="No existen coincidencias para la busqueda" outlined :search-input.sync="filteredChoices.brMunicipality.search" />
                </v-col>
              </template>
              <!-- end BR tax settings fields -->

              <!-- CL ux fields -->
              <template v-if="instance.account.country === 'CL' && customer.country === 'CL'">
                <v-col class="py-0" cols="12">
                  <v-radio-group v-model="uxFields.payerType" @change="setPayerType" row>
                    <v-radio class="mr-8" label="Empresa" value="business" />
                    <v-radio class="mr-8" label="Persona" value="person" />
                  </v-radio-group>
                </v-col>
              </template>
              <!-- end CL ux fields -->

              <v-col class="py-2" cols="6">
                <v-text-field v-model.trim="$v.customer.tax_id.$model" v-dni="customer.country" :color="instance.account.color_background" dense :error="$v.customer.tax_id.$error" hide-details :label="fieldLabels.taxId" maxlength="32" outlined persistent-hint required />
              </v-col>
              <v-col class="py-2" cols="6">
                <v-text-field v-model.trim="$v.customer.zip_code.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.zip_code.$error" hide-details label="Código postal" maxlength="32" outlined persistent-hint required />
              </v-col>
              <v-col class="py-2" cols="12">
                <v-text-field v-model.trim="$v.customer.email.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.email.$error" hide-details label="Email" maxlength="32" outlined persistent-hint required />
              </v-col>

              <!-- BR tax settings fields -->
              <template v-if="instance.account.country === 'BR' && customer.country === 'BR'">
                <v-col class="py-2" cols="6">
                  <v-text-field v-model.trim="$v.customer.tax_settings.br_municipal_tax_number.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.tax_settings.br_municipal_tax_number.$error" hide-details label="Inscripción municipal" maxlength="32" outlined persistent-hint required />
                </v-col>
                <v-col class="py-2" cols="6">
                  <v-select v-model="$v.customer.tax_settings.br_tax_regime.$model" :error="$v.customer.tax_settings.br_tax_regime.$error" :items="choices.brTaxRegimes" item-text="name" item-value="id" outlined background-color="white" label="Régimen tributario" dense hide-details />
                </v-col>
              </template>
              <!-- end BR tax settings fields -->

              <!-- CL tax settings fields -->
              <template v-if="instance.account.country === 'CL' && customer.country === 'CL'">
                <v-col v-if="uxFields.payerType === 'business'" class="py-2" cols="12">
                  <v-text-field v-model.trim="$v.customer.tax_settings.cl_activity_description.$model" background-color="white" :color="instance.account.color_background" dense :error="$v.customer.tax_settings.cl_activity_description.$error" hide-details label="Giro" maxlength="32" outlined persistent-hint required />
                </v-col>
              </template>
              <!-- end CL tax settings fields -->

              <!-- MX tax settings fields -->
              <template v-if="instance.account.country === 'MX' && customer.country === 'MX'">
                <v-col class="py-2" cols="12">
                  <v-text-field v-model.trim="$v.customer.tax_settings.mx_tax_scheme.$model" background-color="white" :color="instance.account.color_background" dense  :error="$v.customer.tax_settings.mx_tax_scheme.$error" hide-details label="Régimen fiscal" maxlength="32" outlined persistent-hint required />
                </v-col>
              </template>
              <!-- end MX tax settings fields -->
            </v-row>
          </v-card-text>

          <v-divider />

          <v-card-text class="px-0 background">
            <v-row class="px-4" no-gutters>
              <v-col cols="12">
                <span class="d-block ml-1 font-weight-medium" :class="{ 'red--text': $v.selectedGateway.$error, 'text--secondary': !$v.selectedGateway.$error }">Elige cómo quieres pagar</span>
              </v-col>
              <v-col cols="12" class="px-0">
                <v-radio-group class="mt-3 mb-4" v-model="selectedGateway" hide-details>
                  <v-list class="pa-0" outlined rounded>
                    <template v-for="(p, i) in instance.gateways">
                      <v-list-item class="my-0" :key="p">
                        <v-list-item-content class="py-0 overflow-visible">
                          <v-radio class="py-3" :color="instance.account.color_background" :value="p">
                            <template v-slot:label>
                              <v-row class="pl-3" align="center" justify="space-between">
                                <img class="ml-4" :alt="p" height="28" :src="require(`@/assets/appAsset-brand--${p}.svg`)" />
                              </v-row>
                            </template>
                          </v-radio>
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider v-if="i < instance.gateways.length - 1" :key="`${p}-divider`" />
                    </template>
                  </v-list>
                </v-radio-group>
              </v-col>
              <v-col cols="12" class="px-0">
                <v-btn @click="goToPay" :color="instance.account.color_background" dark height="40" block>Siguiente</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-sheet>
        <piriod-sign text="cobra con los enlaces de pago de" />
      </v-col>
    </template>
    <!-- end link -->
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { email, maxLength, maxValue, minValue, required, requiredIf } from 'vuelidate/lib/validators'
import { getStatesByCountry } from '../../collections/states'
import { validateTaxId } from '../../locale/tax-id'
import { validateZipCode } from '../../locale/zip-code'
import countries from '../../collections/countries'

import AmountSheet from '../../components/payment-links/AmountSheet'
import PiriodSign from '../..//components/base/PiriodSign'

import GenericViewMixin from '../../mixins/GenericViewMixin'
import WidgetViewMixin from '../../mixins/WidgetViewMixin'

export default {
  name: 'p-payment-link',
  mixins: [
    GenericViewMixin,
    WidgetViewMixin
  ],
  components: {
    AmountSheet,
    PiriodSign
  },
  data: () => ({
    customer: {
      address: '',
      country: null,
      currency: null,
      email: '',
      name: '',
      state: null,
      tax_id: '',
      tax_settings: {
        br_city_code: '',
        br_city_name: '',
        br_municipal_tax_number: '',
        br_tax_regime: '',
        cl_activity_description: '',
        mx_tax_scheme: ''
      },
      zip_code: ''
    },
    choices: {
      brMunicipalities: [],
      brTaxRegimes: [],
      countries: countries,
      states: []
    },
    error: '',
    fieldLabels: {
      name: 'Nombre',
      state: 'Estado',
      taxId: 'ID Fiscal'
    },
    filteredChoices: {
      brMunicipality: {
        search: null,
        items: []
      }
    },
    headers: [
      { value: 'document', text: 'Documento' },
      { value: 'status', text: '' },
      { value: 'due_date', text: 'Vencimiento', align: 'end', sortable: true },
      { value: 'total', text: 'Total', align: 'end', sortable: true },
      { value: 'actions', text: 'Total', align: 'end', sortable: true }
    ],
    loaderMain: false,
    selectedGateway: null,
    uxFields: {
      payerType: 'business'
    }
  }),
  computed: mapState({
    intentAmount: state => state.paymentLinks.linksAmount,
    instance: state => state.paymentLinks.linksDetail
  }),
  created () {
    // check if is intent retrieve
    if (this.$route.query.status) {
      this.$router.push({ name: 'IntentRetrieve'})
    }

    // retrieve payment link data
    this.$store.dispatch('paymentLinks/RETRIEVE', {
      resource: 'publishable/payment_links/links',
      id: this.$route.params.pk
    })
    .then(() => {
      // set default values for customer data
      this.customer.currency = this.instance.currency
      this.customer.country = this.instance.account.country
    })
    .then(() => {
      // handle localization
      if (this.instance.account.country === 'BR') {
        import('../../locale/br')
        .then(module => {
          this.choices.brMunicipalities = module.brMunicipalities
          this.choices.brTaxRegimes = module.brTaxRegimes
        })
        .catch(error => {
          console.error('Error loading one or many BR localization objects: ', error)
        })
      }
    })
    .catch((error) => {
      if (error.response && error.response.status === 404) {
        this.error = 'base_is_not_exist'
      }
    })
    .finally(() => {
      // set languaje
      // this.setLanguaje()

      // destroy loader
      this.loaderMain = false
    })
  },
  watch: {
    'customer.country' (val) {
      this.customer.state = null
      this.choices.states = getStatesByCountry(val)
      this.setFieldLabels()
    }
  },
  methods: {
    goToPay () {
      // validate fields
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }

      // set customer data into store
      const customer = this.customer
      const prefix = `${this.customer.country.toLowerCase()}_`
      customer.tax_settings = Object.fromEntries(
        Object.entries(customer.tax_settings).filter(([k]) => k.startsWith(prefix))
      )

      this.$store.dispatch('paymentLinks/SET_INTENT_CUSTOMER', {
        customer: customer
      })

      // redirect to selected gateway view
      const viewName = `Gateway${this.selectedGateway.charAt(0).toUpperCase()}${this.selectedGateway.slice(1)}`
      this.$router.push({ name: viewName, params: { pk: this.$route.params.pk } })
    },
    listStates () {
      this.$store.dispatch('customerBase/LIST', {
        resource: 'publishable/customer_base/states',
        params: {
          country: this.customer.country,
          page_size: 500
        },
        headers: {
          Authorization: `Session ${this.session.key}`
        }
      })
    },
    setFieldLabels () {
      /**
       * Set field labels names according to customer country
       */
      const def = {
        name: 'Nombre',
        state: 'Estado',
        taxId: 'ID Fiscal'
      }
      this.fieldLabels = def

      const country = this.customer.country
      switch (country) {
        case 'BR':
          this.fieldLabels.nombre = 'Nombre'
          break

        case 'CL':
          this.fieldLabels.name = 'Nombre o empresa'
          this.fieldLabels.state = 'Comuna'
          this.fieldLabels.taxId = 'RUT'
          break

        case 'MX':
          this.fieldLabels.state = 'Estado'
          break

        default:
          this.fieldLabels = def
          break
      }
    },
    setPayerType () {
      if (this.uxFields.payerType === 'person') {
        this.customer.tax_settings.cl_activity_description = 'Persona natural'
        return
      }

      this.customer.tax_settings.cl_activity_description = ''
    }
  },
  validations: {
    customer: {
      address: {
        required,
        maxLength: maxLength(64)
      },
      country: {
        required
      },
      email: {
        required,
        email,
        maxLength: maxLength(254)
      },
      name: {
        required,
        maxLength: maxLength(100)
      },
      state: {
        required
      },
      tax_id: {
        required: requiredIf(function () {
          const country = this.choices.countries.find(country => country.id === this.customer.country)
          return country.hasRegulation
        }),
        taxId: function (val) {
          const country = this.choices.countries.find(country => country.id === this.customer.country)

          if (country.hasRegulation) {
            return validateTaxId(country.id, val)
          }

          return true
        },
        maxLength: maxLength(32)
      },
      tax_settings: {
        br_city_code: {
          required: requiredIf(function () {
            return this.instance.account.country === 'BR' && this.customer.country === 'BR'
          }),
          maxLength: maxLength(12)
        },
        br_municipal_tax_number: {
          maxLength: maxLength(32)
        },
        br_tax_regime: {
          required: requiredIf(function () {
            return this.instance.account.country === 'BR' && this.customer.country === 'BR'
          }),
          maxLength: maxLength(32)
        },
        cl_activity_description: {
          required: requiredIf(function () {
            return this.instance.account.country === 'CL' && this.customer.country === 'CL'
          }),
          maxLength: maxLength(64)
        },
        mx_tax_scheme: {
          required: requiredIf(function () {
            return this.instance.account.country === 'MX' && this.customer.country === 'MX'
          }),
          maxLength: maxLength(3)
        }
      },
      zip_code: {
        zip: function (val) {
          if (this.customer.country) {
            return validateZipCode(this.customer.country, val)
          }

          return true
        }
      }
    },
    intentAmount: {
      required,
      maxValue: function () {
        return this.instance.amount_max ? maxValue(this.instance.amount_max)(this.intentAmount) : () => true
      },
      minValue: function () {
        return this.instance.amount_min ? minValue(this.instance.amount_min)(this.intentAmount) : () => true
      }
    },
    selectedGateway: {
      required
    }
  }
}
</script>

<style>
  .v-dialog__content {
    align-items: start !important;
    justify-content: center !important;
    top: 80px !important;
  }
</style>